<svelte:options
  customElement={{
    tag: "oc-button-v1",
    extend: window.__components.extend({ formAssociated: true }),
    props: {
      base64Href: { type: "String", attribute: "base64-href" },
      disabled: { type: "Boolean", reflect: true },
      fillParent: { type: "Boolean", attribute: "fill-parent", reflect: true },
      fitContent: { type: "Boolean", attribute: "fit-content", reflect: true },
      formAction: { type: "String", attribute: "formaction", reflect: true },
      formMethod: { type: "String", attribute: "formmethod", reflect: true },
      href: { type: "String" },
      iconTypeLeft: { type: "String", attribute: "icon-type-left" },
      iconTypeRight: { type: "String", attribute: "icon-type-right" },
      loading: { type: "String" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label", reflect: true },
      rel: { type: "String" },
      size: { type: "String", reflect: true },
      success: { type: "Boolean", reflect: true },
      target: { type: "String" },
      type: { type: "String" },
      variant: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";
  import { InteractiveElement } from "../../../common/components/InteractiveElement";
  import type { Props } from "./ButtonV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { useFormSubmission } from "./lib/useFormSubmission.svelte";
  import { tooltipContent } from "./lib/utils";
  import { useFormEnterHandler } from "./lib/useFormEnterHandler";
  import { useOverflowCalc } from "./lib/useOverflowCalc.svelte.js";

  let {
    variant = "primary",
    size = "100",
    iconTypeLeft = undefined,
    iconTypeRight = undefined,
    fitContent = false,
    fillParent = false,
    disabled = false,
    type = undefined,
    loading = false,
    success = false,
    ocAriaLabel = undefined,
    href = undefined,
    base64Href = undefined,
    formMethod = undefined,
    formAction = undefined,
    rel = undefined,
    target = undefined,
    internals,
  }: Props & {
    internals: ElementInternals;
    /*  */
    formMethod: Props["formmethod"];
    formAction: Props["formaction"];
  } = $props();

  const Host = $host<HTMLOcButtonV1Element>();

  const isLoading = $derived(
    loading === true || /*                                   */
      loading === "" || /*                                                              */
      loading === "true" || /*                          */
      loading === "keep-native-events",
  );

  let isFitContent = $derived((fitContent && size === "100") || (!fillParent && size === "50"));

  let button = $state<HTMLElement>();

  const overflowState = useOverflowCalc(Host);
  const isOverflowing = $derived(overflowState.value);

  useFormSubmission(
    Host,
    internals,
    () => button,
    () => type,
    () => loading,
    () => isLoading,
  );

  useFormEnterHandler(Host, internals, () => type);
</script>

{#snippet interactiveElement()}
  <InteractiveElement
    {Host}
    asButton={true}
    bind:thisElement={button}
    bind:href
    bind:base64Href
    {disabled}
    {type}
    class={`button button--variant-${variant} ${variant === "secondary-on-color" ? "button--variant-secondary-over-color" : ""} button--size-${size} ${isFitContent ? "button--fit-content" : ""} ${iconTypeLeft ? "button--has-icon-left" : ""} ${iconTypeRight ? "button--has-icon-right" : ""} ${disabled ? "button--disabled" : ""} ${isLoading ? "button--is-loading" : ""} ${success ? "button--success" : ""}`}
    aria-disabled={isLoading || disabled}
    aria-busy={isLoading}
    formmethod={formMethod}
    formaction={formAction}
    {rel}
    {target}
    ocAriaLabel={isLoading ? "Lädt..." : ocAriaLabel}
    onmouseover={overflowState.computeOverflow}
    ontouchstart={overflowState.computeOverflow}
    longpress
  >
    {#snippet children(onslotchange)}
      {#if iconTypeLeft}
        <oc-icon-v1 class="button__icon button__icon--left" type={iconTypeLeft} {size}></oc-icon-v1>
      {/if}

      <span class="button__content" bind:this={overflowState.innerSpan}>
        <slot {onslotchange} />
      </span>

      {#if iconTypeRight}
        <oc-icon-v1 class="button__icon button__icon--right" type={iconTypeRight} {size}
        ></oc-icon-v1>
      {/if}

      {#if isLoading}
        <oc-spinner-v1
          in:fade={{ delay: 300, duration: 200 }}
          class="button__spinner"
          variant="default"
          style:--oc-component-spinner-default-fill-color={variant === "primary" && !success
            ? "var(--oc-base-color-white)"
            : undefined}
          {size}
        ></oc-spinner-v1>
      {/if}
    {/snippet}
  </InteractiveElement>
{/snippet}

{#if isOverflowing}
  <oc-tooltip-v1 class:tooltip--fit-content={isFitContent} position="top">
    {@render interactiveElement()}
    <!-- eslint-disable svelte/no-at-html-tags -->
    {@html tooltipContent(Host)}
  </oc-tooltip-v1>
{:else}
  {@render interactiveElement()}
{/if}

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;
  @use "../../../common/scss/SvelteComponent.mixins" as *;

  :global {
    @mixin overflowEllipsis() {
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
      white-space: nowrap;
    }

    @mixin successState() {
      background-color: tokens.$oc-component-button-success-background-color;
      color: tokens.$oc-component-button-success-text-color;
      font-weight: tokens.$oc-component-button-100-primary-font-weight;

      > .button__icon {
        --color: #{tokens.$oc-component-button-success-icon-color};
      }

      @include interactiveOverlayColors();
    }

    $oc-component-button-100-span-spacing: calc(
      tokens.$oc-component-button-100-icon-size + tokens.$oc-component-button-100-icon-spacing-x
    );
    $oc-component-button-50-span-spacing: calc(
      tokens.$oc-component-button-50-icon-size + tokens.$oc-component-button-50-icon-spacing-x
    );

    :host {
      position: relative;
      --background-color: unset;
      --text-color: unset;
    }

    :host([fit-content]),
    :host([size="50"]) {
      width: fit-content;
    }

    :host([fill-parent]) {
      width: 100%;
    }

    :host([disabled]) {
      pointer-events: none;
    }

    /*                  */
    ::slotted(a),
    ::slotted(button) {
      display: block;
      text-decoration: inherit;
      color: inherit;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      outline: none;
      outline-offset: 0; /*                                        */
      font: inherit;

      @include overflowEllipsis();

      /*                    */
      @include floating-focus.full-bleed();

      /*                                           */
      &:before {
        content: "";
        position: absolute;
        inset: 0;
      }
    }

    /*                                   */
    .button {
      align-items: center;
      border-radius: tokens.$oc-component-button-100-border-radius;
      border: none;

      display: inline-flex;
      font: tokens.$oc-component-button-100-primary-font;
      font-weight: tokens.$oc-component-button-100-primary-font-weight;
      justify-content: center;
      margin: 0;
      min-height: 3rem;
      outline: none;
      outline-offset: 0;
      padding: tokens.$oc-component-button-100-spacing-y
        tokens.$oc-component-button-100-default-spacing-x;
      position: relative;
      text-decoration: none;
      width: 100%;

      &--has-icon-left,
      &--has-icon-right {
        padding: tokens.$oc-component-button-100-spacing-y
          tokens.$oc-component-button-100-icon-spacing-x;

        > span {
          padding: 0 $oc-component-button-100-span-spacing;
        }
      }

      &--fit-content {
        &.button--has-icon-left {
          > span {
            padding: 0 tokens.$oc-component-button-100-icon-spacing-x 0
              $oc-component-button-100-span-spacing;
          }
        }

        &.button--has-icon-right {
          > span {
            padding: 0 $oc-component-button-100-span-spacing 0
              tokens.$oc-component-button-100-icon-spacing-x;
          }
        }

        &.button--has-icon-left.button--has-icon-right {
          > span {
            padding: 0 $oc-component-button-100-span-spacing;
          }
        }
      }

      > .button__icon {
        pointer-events: none;
        height: tokens.$oc-component-button-100-icon-size;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: tokens.$oc-component-button-100-icon-size;

        &--left {
          left: tokens.$oc-component-button-100-icon-spacing-x;
        }

        &--right {
          left: auto;
          right: tokens.$oc-component-button-100-icon-spacing-x;
        }
      }

      @include interactiveOverlayBase();
      @include interactiveOverlayColors();

      &--variant-primary {
        background-color: tokens.$oc-component-button-primary-background-color;
        color: tokens.$oc-component-button-primary-text-color;

        > .button__icon {
          --color: #{tokens.$oc-component-button-primary-icon-color};
        }
      }

      &--variant-secondary {
        background-color: tokens.$oc-component-button-secondary-background-color;
        color: tokens.$oc-component-button-secondary-text-color;
        font: tokens.$oc-component-button-100-secondary-font;

        > .button__icon {
          --color: #{tokens.$oc-component-button-secondary-icon-color};
        }
      }

      &--variant-secondary-over-color {
        background-color: tokens.$oc-component-button-secondary-over-color-background-color;
        color: tokens.$oc-component-button-secondary-over-color-text-color;
        font: tokens.$oc-component-button-100-secondary-font;

        > .button__icon {
          --color: #{tokens.$oc-component-button-secondary-over-color-icon-color};
        }
      }

      &--variant-tertiary {
        background-color: tokens.$oc-component-button-tertiary-background-color;
        color: tokens.$oc-component-button-tertiary-text-color;
        font: tokens.$oc-component-button-100-tertiary-font;

        > .button__icon {
          --color: #{tokens.$oc-component-button-tertiary-icon-color};
        }
      }

      /*                                            */
      &--variant-custom-color-strong {
        background-color: var(
          --background-color,
          #{tokens.$oc-component-button-primary-background-color}
        );
        color: var(--text-color, #{tokens.$oc-component-button-primary-text-color});

        @include interactiveOverlayColors($inverted: true);
      }

      &--variant-custom-color-soft {
        background-color: var(
          --background-color,
          #{tokens.$oc-component-button-primary-background-color}
        );
        color: var(--text-color, #{tokens.$oc-component-button-secondary-text-color});
        font-weight: tokens.$oc-semantic-font-copy-font-weight;
      }

      @media (hover: hover) {
        &:hover:not(:disabled):not(.button--disabled):not(.button--is-loading) {
          @include interactiveOverlayHover();
          cursor: pointer;
        }
      }

      &:active:not(:disabled):not(.button--disabled):not(.button--is-loading) {
        @include interactiveOverlayActive();
        cursor: pointer;
      }

      &:disabled,
      &.button--disabled {
        background-color: tokens.$oc-component-button-disabled-background-color;
        color: tokens.$oc-component-button-disabled-text-color;
        font: tokens.$oc-component-button-100-disabled-font;

        > .button__icon {
          --color: #{tokens.$oc-component-button-disabled-icon-color};
        }
      }

      &.button--success,
      &.button--success.button--disabled,
      &.button--success:disabled {
        @include successState();
      }

      &--size-50 {
        border-radius: tokens.$oc-component-button-50-border-radius;
        min-height: 2rem;
        padding: tokens.$oc-component-button-50-spacing-y
          tokens.$oc-component-button-50-default-spacing-x;

        &.button--has-icon-left {
          padding-top: tokens.$oc-component-button-50-spacing-y;
          padding-right: tokens.$oc-component-button-50-default-spacing-x;
          padding-bottom: tokens.$oc-component-button-50-spacing-y;
          padding-left: calc(tokens.$oc-component-button-50-default-spacing-x / 2);

          > span {
            padding: 0 0 0 $oc-component-button-50-span-spacing;
          }
        }

        &.button--has-icon-right {
          padding-top: tokens.$oc-component-button-50-spacing-y;
          padding-right: calc(tokens.$oc-component-button-50-default-spacing-x / 2);
          padding-bottom: tokens.$oc-component-button-50-spacing-y;
          padding-left: tokens.$oc-component-button-50-default-spacing-x;

          > span {
            padding: 0 $oc-component-button-50-span-spacing 0 0;
          }
        }

        &.button--has-icon-left.button--has-icon-right {
          padding: tokens.$oc-component-button-50-spacing-y
            calc(tokens.$oc-component-button-50-default-spacing-x / 2);

          > span {
            padding: 0 $oc-component-button-50-span-spacing;
          }
        }

        &.button--has-icon-left:not(.button--fit-content),
        &.button--has-icon-right:not(.button--fit-content) {
          padding: tokens.$oc-component-button-50-spacing-y
            calc(tokens.$oc-component-button-50-default-spacing-x / 2);

          span {
            padding: 0 $oc-component-button-50-span-spacing;
          }
        }

        > .button__icon {
          height: tokens.$oc-component-button-50-icon-size;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: tokens.$oc-component-button-50-icon-size;

          &--left {
            left: tokens.$oc-component-button-50-icon-spacing-x;
          }

          &--right {
            left: auto;
            right: tokens.$oc-component-button-50-icon-spacing-x;
          }
        }

        &.button--variant-primary {
          font: tokens.$oc-component-button-50-primary-font;
          font-weight: tokens.$oc-component-button-50-primary-font-weight;
        }

        &.button--variant-secondary {
          font: tokens.$oc-component-button-50-secondary-font;
        }

        &.button--variant-secondary-over-color {
          font: tokens.$oc-component-button-50-secondary-font;
        }

        &.button--variant-tertiary {
          font: tokens.$oc-component-button-50-tertiary-font;
        }

        &:disabled,
        &.button--disabled {
          font: tokens.$oc-component-button-50-disabled-font;
        }

        &.button--success,
        &.button--success.button--disabled,
        &.button--success:disabled {
          @include successState();
        }
      }

      &--is-loading {
        cursor: progress;

        .button__content,
        .button__icon {
          visibility: hidden;
        }
      }
    }

    .button__spinner {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .button__content {
      @include overflowEllipsis();
    }

    oc-tooltip-v1 {
      width: 100%;
    }

    .tooltip--fit-content {
      display: block;
      line-height: 0;
    }
  }
</style>
